import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { growaveConfigurationToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { reviewsIconCollectionManagerContextToken } from "@apps/reviews/context_tokens";
import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
import { reviewsIconCollectionManagerToken } from "@apps/reviews/tokens";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { I18nModule } from "@modules/i18n/I18nModule";
import { container, injectable } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import ReviewsSummaryQuery from "./queries/ReviewsSummaryQuery";
import ReviewsTrustBadgeWidgetWidgetComponent from "./ReviewsTrustBadgeWidget.svelte";
import { ReviewsTrustBadgeWidgetConfigInterface } from "./ReviewsTrustBadgeWidgetConfig";
import ReviewsSummaryService from "./services/ReviewsSummaryService";

@injectable()
class ReviewsTrustBadgeWidget implements WidgetInterface {
    private readonly reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    private readonly reviewsSummaryService: ReviewsSummaryService;
    private readonly reviewsSummaryQuery: ReviewsSummaryQuery;

    constructor(
        private readonly config: ReviewsTrustBadgeWidgetConfigInterface,
        private readonly logger: LoggerInterface
    ) {
        this.logger.debug("ReviewsTrustBadgeWidget is initialized", config);

        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "reviews.ReviewsTrustBadgeWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "reviews.ReviewsTrustBadgeWidget"
            )
        );

        this.reviewsStorefrontValuesService = container.resolve(
            ReviewsStorefrontValuesService
        );

        this.reviewsSummaryService = container.resolve(ReviewsSummaryService);
        this.reviewsSummaryQuery = container.resolve(ReviewsSummaryQuery);
    }

    async init(): Promise<this> {
        const customizationManager = container.resolve(CustomizationManager);
        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );

        const element = this.config.target;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManagerConfig = {
            widgetName: REVIEWS_WIDGET_NAMES.REVIEWS_TRUST_BADGE_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromBlockCustomCss({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const contextMap = new Map();
        contextMap.set(
            reviewsIconCollectionManagerContextToken,
            container.resolve(reviewsIconCollectionManagerToken)
        );

        new ReviewsTrustBadgeWidgetWidgetComponent({
            target: shadowRoot || element,
            props: {
                reviewsStorefrontValuesService:
                    this.reviewsStorefrontValuesService,
                reviewsSummaryQuery: this.reviewsSummaryQuery,
                reviewsSummaryService: this.reviewsSummaryService,
                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
            },
            context: contextMap,
        });

        return this;
    }
}

export default ReviewsTrustBadgeWidget;
